import { useState } from 'react';
import { X, Copy, Download, Check } from 'lucide-react';
import { useStore } from '@/store';
import { trackAction } from '@/lib/analytics';

interface ShareModalProps {
  onClose: () => void;
}

export default function ShareModal({ onClose }: ShareModalProps) {
  const { getShareUrl, exportData, auth } = useStore();
  const [copied, setCopied] = useState(false);
  
  const shareUrl = getShareUrl();
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackAction('share_link_copied');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  const handleDownload = () => {
    const data = exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clocksync-data.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    trackAction('data_exported');
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-bg-light-card dark:bg-bg-card rounded-xl border border-gray-200 dark:border-white/10 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-white/10">
          <h2 className="text-lg font-semibold">Share Your Timeline</h2>
          <button onClick={onClose} className="btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Share via Link */}
          <div className="p-4 bg-bg-light dark:bg-bg rounded-lg border border-gray-200 dark:border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">📤</span>
              <span className="font-medium">Share via Link</span>
              <span className="text-xs text-text-light-muted dark:text-text-muted">(No login required)</span>
            </div>
            
            <p className="text-sm text-text-muted mb-3">
              Your timeline is encoded in this URL. Anyone with the link can view it.
            </p>
            
            <div className="flex gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="input text-xs flex-1"
              />
              <button
                onClick={handleCopyLink}
                className={`btn-primary flex items-center gap-1 ${copied ? 'bg-working' : ''}`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy Link
                  </>
                )}
              </button>
            </div>
            
            <p className="text-xs text-text-light-dim dark:text-text-dim mt-2">
              ⚠️ This is a snapshot. Changes won't sync automatically.
            </p>
          </div>
          
          {/* Export as File */}
          <div className="p-4 bg-bg-light dark:bg-bg rounded-lg border border-gray-200 dark:border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">📥</span>
              <span className="font-medium">Export as File</span>
            </div>
            
            <p className="text-sm text-text-light-muted dark:text-text-muted mb-3">
              Download your data as a JSON file for backup.
            </p>
            
            <button
              onClick={handleDownload}
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download My Timeline
            </button>
          </div>
          
          {/* Live Sharing (requires login) */}
          <div className="p-4 bg-bg-light dark:bg-bg rounded-lg border border-gray-200 dark:border-white/10 opacity-60">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🔄</span>
              <span className="font-medium">Live Sharing</span>
            </div>
            <p className="text-sm text-text-light-muted dark:text-text-muted mb-3">
              Sign in to generate a permanent link that always shows your current time.
            </p>
            
            {auth.isAuthenticated ? (
              <button className="btn-secondary w-full">
                Let Me Create Live Link
              </button>
            ) : (
              <button className="btn-secondary w-full opacity-50 cursor-not-allowed">
                Let Me Sign In to Unlock
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
